import { createContext, useEffect, FC, useState } from 'react';
import { useSnackbar } from 'notistack';
import { getInvoiceVendors, getJobs, getPurchaseOrderVendors, getPunchlistVendors } from '../fetch';
import { ISelectableVendor, IJob } from '../models';

interface IVendorPageProps {
  handleVendorSelect: (val: ISelectableVendor) => void;
  loadingVendors: boolean;
  vendors: ISelectableVendor[];
  vendorName: string | null;
  vendorSageId: number | string | null;

  jobs: IJob[];
  isLoadingJobs: boolean;
}

export const VendorPageContext = createContext<IVendorPageProps>({
  handleVendorSelect: () => {},
  loadingVendors: false,
  vendors: null,
  vendorName: '',
  vendorSageId: null,
  jobs: null,
  isLoadingJobs: false
});

interface IVendorPageContextHandlerProps {}

export const VendorPageContextHandler: FC<IVendorPageContextHandlerProps> = ({
  children
}): JSX.Element => {
  const [vendorName, setVendorName] = useState<string | null>('');
  const [vendorSageId, setVendorSageId] = useState<number | string | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const [vendors, setVendors] = useState<ISelectableVendor[] | null>(null);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);

  const fetchVendors = async () => {
    try {
      setLoadingVendors(true);
      let res;

      if (window.location.href.indexOf('purchase-orders') > -1) {
        res = await getPurchaseOrderVendors();
      } else if (window.location.href.indexOf('punchlists') > -1) {
        res = await getPunchlistVendors();
      } else {
        res = await getInvoiceVendors();
      }

      setVendors([
        {
          name: 'All',
          vendorSageId: undefined
        },
        ...res.records
      ]);
      const lsVendorId = localStorage.getItem('vendorSageId');

      let selected = null;
      if (lsVendorId !== 'null') {
        selected = res.records.find(
          t => t.vendorSageId === parseInt(localStorage.getItem('vendorSageId'))
        );
      }

      // Make sure that if selected was not found, that we set the vendorSageId to null and vendorName to "All"
      if (!selected) {
        localStorage.setItem('vendorSageId', null);
        localStorage.setItem('vendorName', 'All');
        setVendorSageId(null);
        setVendorName('All');
      } else {
        setVendorSageId(lsVendorId !== 'null' ? parseInt(lsVendorId) : null);
        setVendorName(localStorage.getItem('vendorName') ?? 'All');
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Something went wrong. ${error}`, {
        variant: 'error'
      });
    } finally {
      setLoadingVendors(false);
    }
  };

  const fetchJobs = async () => {
    try {
      setIsLoadingJobs(true);
      const res = await getJobs();
      setJobs(res.records);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Error loading jobs, please try again.`, {
        variant: 'error'
      });
    } finally {
      setIsLoadingJobs(false);
    }
  };

  useEffect(() => {
    fetchVendors();
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.href.indexOf('purchase-orders') > -1]);

  const handleVendorSelect = async (val: ISelectableVendor) => {
    if (!val) {
      localStorage.setItem('vendorSageId', null);
      localStorage.setItem('vendorName', 'All');
      setVendorSageId(null);
      // setvalId(null);
      setVendorName('All');
      return;
    }
    localStorage.setItem('vendorSageId', JSON.stringify(val.vendorSageId));
    localStorage.setItem('vendorName', val.name);
    setVendorSageId(val.vendorSageId);
    // setvalId(val.valId);
    setVendorName(val.name);
  };

  return (
    <VendorPageContext.Provider
      value={{
        handleVendorSelect,
        vendorSageId,
        vendorName,
        loadingVendors,
        vendors,
        jobs,
        isLoadingJobs
      }}
    >
      {children}
    </VendorPageContext.Provider>
  );
};
